import { cn } from '../../../lib/cn'
import { EXPLICACAO_DO_PAPEL } from '../../../lib/papeis'
import type { Papel, UsuarioAdministrado } from '../../../types/usuario'

const BASE =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset'

/**
 * Os papéis de uma conta, com a explicação de cada um ao passar o mouse.
 *
 * O `title` carrega a mesma frase que a tela de cadastro mostra por extenso: quem administra vê a
 * lista dezenas de vezes e não deveria precisar abrir o formulário para lembrar o que "CAB_MEMBRO"
 * permite.
 *
 * @param props os papéis concedidos
 * @returns as etiquetas
 */
export function PapeisDaConta({ papeis }: { papeis: Papel[] }) {
  if (papeis.length === 0) {
    return <span className="text-xs text-slate-500">Sem papel — entra e não vê nada</span>
  }
  return (
    <span className="flex flex-wrap gap-1">
      {papeis.map((papel) => (
        <span
          key={papel}
          title={EXPLICACAO_DO_PAPEL[papel].ajuda}
          className={cn(BASE, 'bg-niteroi-50 text-niteroi-900 ring-niteroi-200')}
        >
          {EXPLICACAO_DO_PAPEL[papel].rotulo}
        </span>
      ))}
    </span>
  )
}

/**
 * O estado da conta: se entra, por onde entra e se há troca de senha pendente.
 *
 * Marca só o que foge do normal. Conta ativa, por senha e sem pendência não ganha etiqueta
 * nenhuma — marcar o comum vira ruído em toda linha.
 *
 * @param props a conta
 * @returns as etiquetas de estado
 */
export function EstadoDaConta({ conta }: { conta: UsuarioAdministrado }) {
  return (
    <span className="flex flex-wrap gap-1">
      {!conta.ativo && (
        <span className={cn(BASE, 'bg-slate-100 text-slate-700 ring-slate-300')} title="Esta conta não entra.">
          Inativa
        </span>
      )}
      {conta.deveTrocarSenha && (
        <span
          className={cn(BASE, 'bg-amber-50 text-amber-900 ring-amber-200')}
          title="A pessoa ainda usa a senha provisória e será obrigada a trocá-la no próximo acesso."
        >
          Senha provisória
        </span>
      )}
      {conta.entraVinculado && (
        <span
          className={cn(BASE, 'bg-slate-50 text-slate-700 ring-slate-200')}
          title="Entra pela conta Microsoft da Prefeitura. A senha dela não é gerenciada aqui."
        >
          Login institucional
        </span>
      )}
      {conta.papelContratual === 'CONTRATADA' && (
        <span
          className={cn(BASE, 'bg-purple-50 text-purple-900 ring-purple-200')}
          title="Pessoa da empresa contratada. Ela não enxerga as telas de fiscalização do contrato."
        >
          Contratada
        </span>
      )}
    </span>
  )
}
